import * as React from 'react';
import {
  ITheme,
  IStyle,
  IStyleFunctionOrObject,
  styled,
  mergeStyles,
  concatStyleSetsWithProps
} from 'office-ui-fabric-react';

export const embedMessagingTestId = 'embedMessaging';

export interface EmbedMessagingProps {
  /** Strings to render in the embed message part */
  content: React.ReactNode;

  /** The actions you want to render alongside the content */
  actions?: React.ReactNode;

  /** Theme (provided through customization.) */
  theme?: ITheme;

  /** Call to provide customized styling */
  styles?: IStyleFunctionOrObject<EmbedMessagingStyleProps, EmbedMessagingStyles>;

  /** A description of the messaging part content for the benefit of screen readers */
  ariaLabel?: string;
}

export interface EmbedMessagingStyles {
  /** Style set for the root element. */
  root?: IStyle;

  /** Style set for the element containing the icon, text, and optional dismiss button. */
  content?: IStyle;

  /** Style set for the optional element containing the action elements. */
  actions?: IStyle;
}

export interface EmbedMessagingStyleProps extends Pick<EmbedMessagingProps, 'theme'> {}

/**
 * Template dialog-like messaging within the BackplateComponentSpace.
 * It provides slots for rendering content message and action
 */
export const EmbedMessaging = React.memo(
  styled((props: EmbedMessagingProps) => {
    const { content, actions, ariaLabel, styles } = props;

    const resolvedStyles = concatStyleSetsWithProps(props, styles);

    const bannerClassName = mergeStyles(resolvedStyles.root);
    const contentClassName = mergeStyles(resolvedStyles.content);
    const actionsClassName = mergeStyles(resolvedStyles.actions);

    return (
      <div className={bannerClassName} data-testid={embedMessagingTestId}>
        <div className={contentClassName} tabIndex={0} aria-label={ariaLabel}>
          {content}
        </div>
        {actions && <div className={actionsClassName}>{actions}</div>}
      </div>
    );
  }, getDefaultStyle)
);

function getDefaultStyle(props: EmbedMessagingStyleProps): EmbedMessagingStyles {
  const { theme } = props;

  return {
    root: {
      padding: 12,
      position: 'relative',
      fontFamily: 'Segoe UI',
      fontStyle: 'normal',
      fontWeight: 'normal',
      display: 'flex',
      flexFlow: 'row wrap',
      justifyContent: 'space-between',
      textAlign: 'initial',
      overflow: 'scroll',
      backgroundColor: theme?.semanticColors.bodyBackground,
      border: 'none',
      selectors: {
        '::-webkit-scrollbar': {
          height: 0,
          width: 2,
          backgroundColor: theme?.semanticColors.bodyBackground
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: theme?.semanticColors.buttonBorder,
          borderRadius: 2
        }
      }
    },
    content: {
      flexGrow: 1,
      padding: 8,
      alignContent: 'stretch',
      color: theme?.semanticColors.bodyText
    },
    actions: {
      padding: '0 8px 0 8px',
      display: 'flex',
      flexFlow: 'row wrap',
      justifyContent: 'flex-start',
      alignSelf: 'flex-start',
      selectors: {
        '& button': {
          marginBottom: 4,
          marginTop: 4
        },
        '& button:nth-child(n+1)': {
          marginRight: 8
        }
      }
    }
  };
}
